import RulerIcon from 'utils/icon/icon-ruler.svg';
import { h } from 'preact';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  isMelanieLyne,
  sortProductFits,
  sortProductSizes,
  isProductAccessory,
} from 'utils/helpers';
import middleware from 'utils/middleware';
import { withProductContext } from './product-context';
import { getInseamValue } from '../utils/laura-one';

const OptionValue = ({
  optionId,
  option,
  value,
  selectedOption,
  variants,
  onChange,
  inputPrefix,
  isQuickView,
  isLookOverlay,
  options,
  relatedProducts,
  useTheRegularInventory,
}) => {
  const [mainLocationIsOutOfStock, setMainLocationIsOutOfStock] = useState();
  const [currentOptionVariant, setCurrentOptionVariant] = useState();

  useEffect(() => {
    if (!currentOptionVariant) return;
    const currentOptionVariantId =
      currentOptionVariant?.id.split('/ProductVariant/')[1];

    if (!currentOptionVariantId) return;
    const getInventory = async () => {
      const response = await middleware.getVariantInventory(
        currentOptionVariantId,
      );
      if (!response.data) {
        // eslint-disable-next-line no-console
        console.error(
          'variant-selector.js: Middleware request failed',
          response,
        );
        return [];
      }
      return response.data.productVariant.inventoryItem.inventoryLevel
        .quantities[0].quantity;
    };

    if (!useTheRegularInventory) {
      getInventory(currentOptionVariantId).then((availableQuantity) => {
        const isAvailable = availableQuantity > 0;
        setMainLocationIsOutOfStock(!isAvailable);
      });
    }
  }, [currentOptionVariant]);

  const { isOutOfStock, variantExists } = useMemo(() => {
    const currentOptions = options
      .map((o) => (o.name === option.name ? { name: option.name, value } : o))
      .reduce(
        (items, item) => ({
          ...items,
          ...{
            [item.name]: item.value,
          },
        }),
        {},
      );

    const variant = variants.find((v) =>
      v.selectedOptions.every((opt) => currentOptions[opt.name] === opt.value),
    );

    let outOfStock = false;
    if (
      option.name.trim().toLowerCase() ===
      window.TMW.Strings.product.options.fit
    ) {
      outOfStock = !relatedProducts
        .reduce(
          (fitVariants, item) => [
            ...fitVariants,
            ...item.variants.filter((itemVariant) =>
              itemVariant.selectedOptions.find(
                (itemOption) =>
                  itemOption.name.trim().toLowerCase() ===
                    window.TMW.Strings.product.options.fit &&
                  itemOption.value === value,
              ),
            ),
          ],
          [],
        )
        .some((item) => item.availableForSale);
    }

    setCurrentOptionVariant(variant);

    return {
      isOutOfStock: outOfStock,
      variantExists: !!variant || options.some((item) => !item.value),
    };
  }, [options, value]);

  return (
    <div
      className={clsx('product-option group', {
        'min-w-40 h-40 ml:min-w-30 ml:w-auto ml:h-30':
          option.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.size,
        disabled:
          (!variantExists &&
            option.name.trim().toLowerCase() !==
              window.TMW.Strings.product.options.fit) ||
          isOutOfStock ||
          mainLocationIsOutOfStock,
        '[&.disabled]:opacity-30': isLookOverlay,
      })}
    >
      <input
        type="radio"
        value={value}
        className="peer hidden"
        name={`${inputPrefix}-${optionId}`}
        data-option-name={option.name}
        onChange={onChange}
        id={`${inputPrefix}-${optionId}-${value}`}
        checked={selectedOption?.value === value}
      />
      <label
        htmlFor={`${inputPrefix}-${optionId}-${value}`}
        className={clsx('peer-checked:border-black', {
          'peer-checked:!bg-transparent lc:peer-checked:!bg-black lc:peer-checked:!text-white lc:hover:!bg-black lc:hover:!text-white lc:peer-checked:hover:!bg-black lc:peer-checked:hover:!text-white lc:group-[.disabled]:hover:!bg-transparent lc:group-[.disabled]:hover:!text-disabled lc:group-[.disabled]:hover:!border-grey-400':
            isQuickView,
          'lc:peer-checked:text-white lc:peer-checked:bg-black lc:group-[.disabled]:peer-checked:text-white':
            !isQuickView,
          '!px-16':
            option.name.trim().toLowerCase() !==
              window.TMW.Strings.product.options.size &&
            option.name.trim().toLowerCase() !==
              window.TMW.Strings.product.options.color,
        })}
      >
        <div className="w-full text-center">{value}</div>
      </label>
    </div>
  );
};

const VariantSelector = ({
  productState,
  productActions,
  option,
  options,
  setOptions,
  product,
  relatedProducts,
  inputPrefix,
  isQuickView = false,
  isLookOverlay = false,
}) => {
  const optionId = `product-option-${option.name}`;
  const selectedOption = (options || []).find((o) => o.name === option.name);
  const { fits } = productState;
  const hasThemeLC = document.body.classList.contains('theme-lc');

  // eslint-disable-next-line max-len
  if (
    option?.name?.trim().toLowerCase() ===
    window.TMW.Strings.product.options.fit
  ) {
    if (isMelanieLyne()) {
      return null;
    }
    if (
      option?.values?.length === 1 &&
      (option?.values[0]?.trim().toLowerCase() === 'laura' ||
        option?.values[0]?.trim() === 'Melanie Lyne') &&
      isProductAccessory(product)
    ) {
      return null;
    }
  }

  const sizeGuideHandles = useMemo(() => {
    if (!product) return [];
    const sizeGuideHandle = [product.sizeGuide];

    const relatedHandles = relatedProducts
      .filter((item) => item.sizeGuide)
      .map((item) => item.sizeGuide);

    const combinedArray = [...new Set(relatedHandles.concat(sizeGuideHandle))];

    return combinedArray;
  }, [relatedProducts]);

  const sortedFits = useMemo(
    () => (!fits ? [] : sortProductFits(fits)),
    [fits],
  );

  const inseamLabel = useMemo(
    () =>
      getInseamValue(
        product?.productTypeDetails?.value,
        product?.inseam?.value,
      ),
    [product],
  );

  const values =
    option.name.trim().toLowerCase() === window.TMW.Strings.product.options.fit
      ? sortedFits
      : option.name.trim().toLowerCase() ===
        window.TMW.Strings.product.options.size
      ? sortProductSizes(option.values)
      : option.values;

  if (
    values.length < 2 &&
    option.name.trim().toLowerCase() !==
      window.TMW.Strings.product.options.fit &&
    option.name.trim().toLowerCase() !== window.TMW.Strings.product.options.size
  ) {
    return null;
  }

  const label = useMemo(() => {
    if (
      option.name.trim().toLowerCase() ===
      window.TMW.Strings.product.options.size
    ) {
      if (product?.vanitySize?.value) {
        return product.vanitySize.value;
      }
    }

    if (
      option.name.trim().toLowerCase() ===
      window.TMW.Strings.product.options.fit
    ) {
      const fit = window.TMW.fitLabels[selectedOption?.value.toLowerCase()];

      if (fit) {
        const inseamText = inseamLabel
          ? Shopify.locale === 'fr'
            ? ` • Entrejambe : ${inseamLabel} po`
            : ` • Inseam ${inseamLabel}"`
          : '';

        return `<div>${option.name}</div>
                <div class='body-3 text-default'>
                  ${fit}${inseamText}
                </div>`;
      }
    }

    return option.name;
  }, [product, isQuickView, option, options, selectedOption]);

  const handleOptionChange = (e) => {
    const updatedOption = e.target.dataset.optionName;
    const selectedOptions = options.map((o) =>
      o.name === updatedOption
        ? { name: updatedOption, value: e.target.value }
        : o,
    );
    setOptions([...selectedOptions]);
    if (
      updatedOption.trim().toLowerCase() ===
      window.TMW.Strings.product.options.fit
    ) {
      const currentOptions = options.reduce(
        (items, item) => ({
          ...items,
          ...{
            [item.name]: item.value,
          },
        }),
        {},
      );
      const productsByFit = productState.relatedProducts.filter((item) =>
        item.options
          .find(
            (opt) =>
              opt.name.trim().toLowerCase() ===
              window.TMW.Strings.product.options.fit,
          )
          ?.values.includes(e.target.value),
      );
      productActions.setProducts(productsByFit);
      let newProduct = productsByFit.find((item) =>
        item.variants.find((v) =>
          v.selectedOptions.every(
            (opt) =>
              currentOptions[opt.name] === opt.value ||
              !currentOptions[opt.name],
          ),
        ),
      );
      if (!newProduct) {
        [newProduct] = productsByFit;
      }
      productActions.setCurrentProduct(newProduct);
      if (!isQuickView && !isLookOverlay) {
        document.dispatchEvent(
          new CustomEvent('productUpdated', {
            detail: newProduct,
          }),
        );
      }
    }
  };

  return (
    <div
      className={`${
        isQuickView
          ? 'pb-24 ml:flex ml:flex-col ml:items-center'
          : isLookOverlay &&
            option.name.trim().toLowerCase() ===
              window.TMW.Strings.product.options.fit
          ? 'w-full lc:md:max-w-271 ml:md:max-w-332 ml:mb-20'
          : isLookOverlay &&
            option.name.trim().toLowerCase() !==
              window.TMW.Strings.product.options.fit
          ? 'w-full'
          : 'py-16 lc:lg:py-20'
      }`}
      id={
        hasThemeLC &&
        option.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.fit
          ? 'fit-selector'
          : null
      }
    >
      <label
        htmlFor={optionId}
        className={clsx('flex gap-8 body-3 text-subdued relative w-full', {
          'lc:items-end pb-12 justify-between ml:justify-center':
            option.name.trim().toLowerCase() ===
            window.TMW.Strings.product.options.size,
          'pb-8':
            option.name.trim().toLowerCase() !==
            window.TMW.Strings.product.options.size,
          'ml:justify-center': isQuickView,
          'lc:body-2':
            isLookOverlay &&
            option.name.trim().toLowerCase() !==
              window.TMW.Strings.product.options.size,
          'lc:body-3 lc:pt-24':
            isLookOverlay &&
            option.name.trim().toLowerCase() ===
              window.TMW.Strings.product.options.size,
        })}
      >
        <div
          dangerouslySetInnerHTML={{ __html: label }}
          className={clsx(
            'flex gap-8 items-end flex-wrap', {
            '!opacity-0':
              option.name.trim().toLowerCase() ===
                window.TMW.Strings.product.options.size &&
              isLookOverlay &&
              isMelanieLyne(),
          })}
        ></div>
        {option.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.size &&
          product?.sizeGuide && (
            <button
              className={
                'flex items-end text-black gap-4 ml:items-center body-3-underline hover:no-underline ml:absolute ml:bottom ml:right-0'
              }
              type="button"
              onClick={() => {
                document.dispatchEvent(
                  new CustomEvent('size-guide:open', {
                    detail: {
                      handles: sizeGuideHandles,
                    },
                  }),
                );
              }}
            >
              <RulerIcon className="w-24 h-atuo ml:hidden" />
              <div>{window.TMW.Strings.product.sizeGuide}</div>
            </button>
          )}
      </label>
      <div
        id={optionId}
        className={
          isQuickView
            ? clsx('flex flex-wrap', {
                'ml:justify-center gap-6 lc:gap-16 lg:gap-8 lc:lg:gap-[19.85px]':
                  option.name.trim().toLowerCase() !==
                  window.TMW.Strings.product.options.fit,
                'gap-12':
                  option.name.trim().toLowerCase() ===
                  window.TMW.Strings.product.options.fit,
              })
            : isLookOverlay
            ? clsx('flex flex-wrap', {
                'gap-[7.43px] lg:gap-[26.29px] lc:gap-[17.3px] lc:lg:gap-[19.85px]':
                  option.name.trim().toLowerCase() !==
                  window.TMW.Strings.product.options.fit,
                'gap-12':
                  option.name.trim().toLowerCase() ===
                  window.TMW.Strings.product.options.fit,
              })
            : clsx({
                'flex flex-wrap gap-16 ml:justify-center':
                  option.name.trim().toLowerCase() !==
                  window.TMW.Strings.product.options.fit,
                'grid grid-cols-3 gap-8':
                  option.name.trim().toLowerCase() ===
                  window.TMW.Strings.product.options.fit,
              })
        }
      >
        {values.map((value) => (
          <OptionValue
            value={value}
            optionId={optionId}
            option={option}
            relatedProducts={relatedProducts}
            variants={product.variants}
            selectedOption={selectedOption}
            inputPrefix={inputPrefix}
            onChange={handleOptionChange}
            key={`${optionId}-${value}`}
            isQuickView={isQuickView}
            isLookOverlay={isLookOverlay}
            options={options}
            useTheRegularInventory={product.useTheRegularInventory?.value}
          />
        ))}
      </div>
      {
        option.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.size && (
          <div
            className={clsx('predict-placement', {
              'left-aligned-shopthelook': isMelanieLyne() && isLookOverlay,
            })}
            data-predict-product-key={product.handle}
            data-predict-product-name={product.title}
            data-predict-product-img={product?.images?.[0]?.src}
          />
        )
      }
    </div>
  );
};
export default withProductContext(VariantSelector);
